/**
 * Certification tiers — the officer-facing ladder (W9 rhythm).
 *
 * One authority for "what tier am I, what score do I beat next". Derived
 * from CERTIFICATION.TIER_THRESHOLDS (the /10 report scale). Purely about
 * the certified rolling average from full sessions — mini-mode evidence
 * never enters here (purity = credibility).
 */
import { CERTIFICATION } from '../constants/index.js';
/** Ascending ladder, thresholds from the constants (single number source). */
export const CERT_TIERS = [
    { key: 'bronze', name: 'Bronze', threshold: CERTIFICATION.TIER_THRESHOLDS.BRONZE },
    { key: 'silver', name: 'Silver', threshold: CERTIFICATION.TIER_THRESHOLDS.SILVER },
    { key: 'gold', name: 'Gold', threshold: CERTIFICATION.TIER_THRESHOLDS.GOLD },
    { key: 'platinum', name: 'Platinum', threshold: CERTIFICATION.TIER_THRESHOLDS.PLATINUM },
    { key: 'diamond', name: 'Diamond', threshold: CERTIFICATION.TIER_THRESHOLDS.DIAMOND },
];
/** The tier a certified average (/10) currently earns. */
export function certTier(averageScore) {
    let earned = CERT_TIERS[0];
    for (const tier of CERT_TIERS) {
        if (averageScore >= tier.threshold)
            earned = tier;
    }
    return earned;
}
/** The one-glance answer: where I am, what average moves me up. */
export function certPath(averageScore) {
    const tier = certTier(averageScore);
    const idx = CERT_TIERS.findIndex((t) => t.key === tier.key);
    const next = idx + 1 < CERT_TIERS.length ? CERT_TIERS[idx + 1] : null;
    return { tier, next, scoreToBeat: next ? next.threshold : null };
}
// ---------------------------------------------------------------------------
// Rolling-window certification (W10) — belt semantics
// ---------------------------------------------------------------------------
/** Sessions in the rolling average per rung (from the constants). */
export const TIER_WINDOWS = {
    bronze: CERTIFICATION.ROLLING_WINDOW.BRONZE,
    silver: CERTIFICATION.ROLLING_WINDOW.SILVER,
    gold: CERTIFICATION.ROLLING_WINDOW.GOLD,
    platinum: CERTIFICATION.ROLLING_WINDOW.PLATINUM,
    diamond: CERTIFICATION.ROLLING_WINDOW.DIAMOND,
};
function avgOf(scores, n) {
    const slice = scores.slice(0, n);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
}
/**
 * Belt semantics: the tier is the HIGHEST rung whose full window is met —
 * to be Gold you must average ≥ its threshold over your last 20 scored
 * calls, not over a lucky 8. Bronze is the entry rung from the first
 * scored call. Recent effort always moves the needle (the ladder never
 * goes cold at volume), and a small hot sample can't inflate rank.
 *
 * `recentScores` = clean-report ratings, most recent first.
 */
export function rollingCertification(recentScores) {
    const count = recentScores.length;
    let earned = CERT_TIERS[0]; // bronze — the entry rung
    for (const rung of CERT_TIERS.slice(1)) {
        const window = TIER_WINDOWS[rung.key];
        if (count >= window && avgOf(recentScores, window) >= rung.threshold) {
            earned = rung;
        }
        else {
            break; // rungs are ascending; the first miss ends the climb
        }
    }
    const idx = CERT_TIERS.findIndex((t) => t.key === earned.key);
    const next = idx + 1 < CERT_TIERS.length ? CERT_TIERS[idx + 1] : null;
    const window = TIER_WINDOWS[earned.key];
    return {
        tier: earned,
        next,
        scoreToBeat: next ? next.threshold : null,
        nextWindow: next ? TIER_WINDOWS[next.key] : null,
        window,
        windowAvg: count > 0 ? avgOf(recentScores, window) : null,
        sessionCount: count,
    };
}
//# sourceMappingURL=certification.js.map